import React, { Component } from 'react';
import { connect } from 'react-redux';

// Components
import { Button, Card, Col, Empty, message, Progress, Row, Select, Spin, Switch, Table, Tooltip, Upload } from 'antd';
import MeterMultiSelectBox from '../common/MeterMultiSelectBox';
import PeriodSelectModal from './PeriodSelectModal';
import SvgSelectModal from './SvgSelectModal';
import EnergyFlow from '../energyFlow';
import { Bar, HorizontalBar, Line } from 'react-chartjs-2';
import { fillBox, fillBoxContent, graphColors } from '../../constants/index';

// Actions
import { fetchAllMeters, fetchMeterTypes } from '../../data/meters/actions';
import {
    onChangeVisiblePeriod,
    setLastLegendVisibility,
    setLastPreselectedMeter,
    setLastPreselectedRefresh,
    setSelectedChartMode,
    setSelectedEnergyFlowPosition,
    setSelectedMeterIds,
} from '../../data/app/actions';
import { fetchAllDevices } from '../../data/devices/actions';
import { fetchAllDeviceTypes } from '../../data/deviceTypes/actions';
import {
    addUrlAndToken,
    fetchMonitoringRawData, onClearRawData,
    onMonitoringIsLive, onMonitoringIsLoading,
    onMonitoringSetBounds,
    onMonitoringSetLastTs
} from '../../data/monitoring/actions';
import { fetchAllSwitches, onSelectSwitch } from '../../data/switch/actions';

// Selectors
import { getMetersListSortedForMonitoring, getValidCloudPlan } from '../../data/meters/selectors';
import {
    getLegendsVisibility,
    getMonitoringVisiblePeriod,
    getPreSelectedChartMode,
    getPreSelectedEnergyFlowPosition,
    getPreselectedMeterId,
    getPreselectedMeterIds,
    getPreselectedRefresh,
} from '../../data/app/selector';
import { getUserId, isRoleAdministrator } from '../../data/auth/selectors';
import {
    getMonitoringAddonGraphData,
    getMonitoringBounds,
    getMonitoringBoundsMoment,
    getMonitoringGraphData,
    getMonitoringIsLive,
    getMonitoringIsLoading,
    getMonitoringRawData,
    getMonitoringSingleGraphData,
    getServerTimeDifference,
} from '../../data/monitoring/selectors';
import { getSelectedSwitch, getSwitchList, getSwitchListIsLoading } from '../../data/switch/selectors';

// Others
import { strings } from '../../localization';
import moment from 'moment';
import { generateDateTimeString, getOnlySubmetersIfExists, periods } from '../../utils';
import { fromJS } from 'immutable';
import MeterSelectBox from '../common/MeterSelectBox';
import ExcelSelectModal from './ExcelSelectModal';
import { CHART_MODE } from '../../data/monitoring/constants';

class Monitoring extends Component {

    mobilePreventTouchScrollBox = {
        position: 'absolute',
        width: '-webkit-fill-available',
        top: 0,
        backgroundColor: '#00000000',
        height: '-webkit-fill-available',
        margin: '2px',
        display: 'flex'
    };

    options = {
        maintainAspectRatio: false,
        aspectRatio: 2,
        legend: {
            display: true,
            position: 'top',
            labels: {
                filter: item => {
                    const { isMobile } = this.props;

                    return isMobile ? !item.text.includes('HIDDEN') && !item.text.includes('VAR') : !item.text.includes('HIDDEN');
                },
                boxWidth: 12,
                padding: 25
            },
            onClick: (e, legendItem) => {
                const {
                    legendsVisibility,
                    setLastLegendVisibility
                } = this.props;
                legendsVisibility[legendItem.datasetIndex] = !legendsVisibility[legendItem.datasetIndex];
                setLastLegendVisibility(fromJS(legendsVisibility));
            }
        },
        scales: {
            xAxes: [{
                type: 'time',
                time: {
                    parser: 'MM/DD/YYYY HH:mm',
                    tooltipFormat: 'll HH:mm:ss',
                    unit: 'second'
                }
            }],
            yAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: strings.training.powerUsageLabel,
                    fontColor: '#555',
                    fontStyle: 'bold'
                },
                ticks: {
                    beginAtZero: true,
                    callback: value => value >= 10000 ? `${Math.round(value/1000)} k` :
                        value >= 1000 ? `${Math.round(value/100)/10} k` : `${Math.round(value)}`
                },
                stacked: false
            }]
        },
        tooltips: {
            displayColors: true,
            position: 'nearest',
            intersect: false,
            callbacks: {
                afterLabel: (tooltipItem, data) => {
                    const dev = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                    return dev.device ? `${Math.round(dev.wt * 100) / 100} ${strings.training.wattUnit}  ${Math.round(dev.vr * 100) / 100} ${strings.training.var}` : null;
                },
                label: (tooltipItem, data) => {
                    const dev = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                    return dev.device ? this.getDevName(dev.device) : `${Math.round(dev.y * 100) / 100}`;
                },
                beforeFooter: (tooltipItem, data) => {
                    const dev = data.datasets[tooltipItem[0].datasetIndex].data[tooltipItem[0].index];
                    const { lang } = this.props;
                    return dev.device ? generateDateTimeString(new Date(dev.x), true, lang) : null;
                },
                footer: (tooltipItem, data) => {
                    const dev = data.datasets[tooltipItem[0].datasetIndex].data[tooltipItem[0].index];
                    const { lang } = this.props;
                    return dev.device ? generateDateTimeString(new Date(dev.x + dev.duration), true, lang) : null;
                }
            }
        },
        elements: {
            point: {
                radius: context => {
                    const rec = context.dataset.data[context.dataIndex];
                    return rec ? rec.device ? rec.device < 0 ? 20 : 10 : 0 : 0;
                },
                hoverRadius: context => {
                    const rec = context.dataset.data[context.dataIndex];
                    return rec ? rec.device ? rec.device < 0 ? 22 : 12 : 0 : 0;
                },
                backgroundColor: context => {
                    const rec = context.dataset.data[context.dataIndex];
                    if (rec && rec.device)
                        switch (rec.phase) {
                            case 1:
                                return graphColors.p1_fill;
                            case 2:
                                return graphColors.p2_fill;
                            case 3:
                                return graphColors.p3_fill;
                            default:
                                return graphColors.oa_fill;
                        }
                    else
                        return context.backgroundColor;
                },
                borderColor: context => {
                    const rec = context.dataset.data[context.dataIndex];
                    if (rec && rec.device)
                        switch (rec.phase) {
                            case 1:
                                return graphColors.p1_line;
                            case 2:
                                return graphColors.p2_line;
                            case 3:
                                return graphColors.p3_line;
                            default:
                                return graphColors.oa_line;
                        }
                    else
                        return context.borderColor;
                },
                hoverBorderWidth: 3
            }
        },
        layout: {
            padding: {
                left: 0,
                right: 40,
                top: 0,
                bottom: 0
            }
        },
        animation: {
            duration: 0
        }
    };

    optionsDevice = {
        ...this.options,
        scales: {
            ...this.options.scales,
            yAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: strings.training.powerUsageLabel,
                    fontColor: '#555',
                    fontStyle: 'bold'
                },
                ticks: {
                    beginAtZero: true,
                    callback: value => value >= 10000 ? `${Math.round(value/1000)} k` :
                        value >= 1000 ? `${Math.round(value/100)/10} k` : `${Math.round(value)}`
                },
                stacked: true
            }]}
    }

    singleOptions = {
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                filter: item => !item.text.includes('HIDDEN'),
                boxWidth: 12,
                padding: 25
            }
        },
        scales: {
            xAxes: [{
                type: 'time',
                time: {
                    parser: 'MM/DD/YYYY HH:mm',
                    tooltipFormat: 'll HH:mm',
                    unit: 'minute'
                }
            }],
            yAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: strings.training.singleGraphYLabel,
                    fontColor: '#555',
                    fontStyle: 'bold'
                },
                ticks: {
                    beginAtZero: true,
                    callback: value => `${Math.round(value * 100) / 100}`
                }
            }]
        }
    };

    addonOptions = {
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                filter: item => !item.text.includes('HIDDEN'),
                boxWidth: 12,
                padding: 25
            }
        },
        scales: {
            xAxes: [{
                type: 'time',
                time: {
                    parser: 'MM/DD/YYYY HH:mm',
                    tooltipFormat: 'll HH:mm:ss',
                    unit: 'minute'
                }
            }],
            yAxes: [{
                scaleLabel: {
                    display: true,
                    // labelString is set dynamically in render()
                    fontColor: '#555',
                    fontStyle: 'bold'
                },
                ticks: {
                    beginAtZero: true,
                    callback: value => `${Math.round(value * 100) / 100}`
                }
            }]
        }
    };

    barOptions = {
        maintainAspectRatio: false,
        aspectRatio: 2,
        legend: {
            display: false
        },
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    callback: value => `${Math.round(value * 10000) / 10000} ${strings.dashboard.kwhUnit}`
                }
            }]
        },
        layout: {
            padding: {
                left: 5,
                right: 5,
                top: 5,
                bottom: 5
            }
        }
    };

    usageOptions = {
        maintainAspectRatio: false,
        aspectRatio: 2,
        legend: {
            display: false
        },
        scales: {
            xAxes: [{
                ticks: {
                    beginAtZero: true,
                    callback: value => `${Math.round(value * 10000) / 10000} ${strings.dashboard.kwhUnit}`
                }
            }],
            yAxes: [{
                scaleLabel: {
                    display: true,
                    labelString: strings.dashboard.pvEfficiency,
                    fontColor: '#555',
                    fontStyle: 'bold'
                },
                ticks: {
                    display: false
                },
                gridLines:{
                    display: false
                },
                categoryPercentage: 0.5,
                barPercentage: 1.0,
                maxBarThickness: 30
            }]
        },
        layout: {
            padding: {
                left: 5,
                right: 5,
                top: 5,
                bottom: 5
            }
        }
    };

    recognitionsColumns = [
        {
            title: strings.training.recognitionTblNameCol,
            dataIndex: 'name',
            key: 'name'
        },{
            title: strings.training.recognitionTblCountCol,
            dataIndex: 'count',
            key: 'count'
        },{
            title: strings.training.recognitionTblWattHoursCol,
            dataIndex: 'wh',
            key: 'wh'
        },{
            title: strings.training.recognitionTblWattCol,
            dataIndex: 'watt',
            key: 'watt'
        },{
            title: strings.training.recognitionTblPercentCol,
            dataIndex: 'wattPercent',
            key: 'wattPercent'
        }
    ];

    recognitionsColumnsMobile = [
        {
            title: strings.training.recognitionTblNameCol,
            dataIndex: 'name',
            key: 'name'
        },{
            title: strings.training.recognitionTblWattCol,
            dataIndex: 'watt',
            key: 'watt'
        },{
            title: strings.training.recognitionTblPercentCol,
            dataIndex: 'wattPercent',
            key: 'wattPercent'
        }
    ];

    alwaysOnColumns = [
        {
            title: strings.training.alwaysOnTblDayCol,
            dataIndex: 'day',
            key: 'day'
        },{
            title: strings.training.alwaysOnTblP1Col,
            dataIndex: 'p1',
            key: 'p1'
        },{
            title: strings.training.alwaysOnTblP2Col,
            dataIndex: 'p2',
            key: 'p2'
        },{
            title: strings.training.alwaysOnTblP3Col,
            dataIndex: 'p3',
            key: 'p3'
        }
    ];

    chart = React.createRef();

    constructor(props){
        super(props);
        this.loop = null;
        this.isDragging = false;
        this.dragStartPoint = 0;
        this.dragFrom = 0;
        this.dragTo = 0;
        this.dragBox = React.createRef();
        this.lastDragFrom = 0;
        this.dragged = false;
    }

    state = {
        selectedMeter: null,
        selectedMeters: null,
        showPeriodSelect: false,
        showSvgSelect: false,
        showExcelSelect: false,
        energyFlowPosition: 'right',
        width: window.innerWidth
    };

    componentWillMount() {
        const {
            fetchAllMeters,
            fetchAllDeviceTypes,
            metersData,
            isMobile,
            setLastPreselectedRefresh,
            setLastPreselectedMeter,
            setSelectedMeterIds,
            preselectedEnergyFlowPosition,
            fetchMeterTypes
        } = this.props;

        if (isMobile === true) {
            this.setState({energyFlowPosition: 'up'});
            const {location} = window;
            if (location) {
                const {search} = location;
                const query = new URLSearchParams(search);
                const refreshPeriod = query.get('refreshPeriod');
                const meterId = query.get('meterId');

                setLastPreselectedMeter(meterId);
                setSelectedMeterIds([meterId]);
                setLastPreselectedRefresh(refreshPeriod);
            }
        } else{
            this.setState({energyFlowPosition: preselectedEnergyFlowPosition});
        }

        fetchAllDeviceTypes();
        fetchMeterTypes();

        if(!metersData) fetchAllMeters(); else {
            this.selectMeter(this.props);
        }

        window.addEventListener('resize', this.updateDimensions);
    }

    componentWillReceiveProps(nextProps) {
        this.selectMeter(nextProps);
    }

    componentWillUnmount() {
        clearTimeout(this.loop);
        window.removeEventListener('resize', this.updateDimensions);
    }

    updateDimensions = () => {
        this.setState({ width: window.innerWidth });
    };

    getSecondGraphLabel = (selectedValue) => {
        switch (selectedValue) {
            case -6:
                return strings.dashboard.premiumVoltage;
            case -5:
                return strings.dashboard.premiumApparentPower;
            case -4:
                return strings.dashboard.premiumCurrent;
            case -3:
                return strings.dashboard.premiumCosPhi;
            default:
                return null;
        }
    }

    getDevName = dev => {
        const { rawData } = this.props;
        if(dev < 0 || !rawData.recognitions) return strings.training.unknownDevice;
        const devices = rawData.recognitions.filter(item => item.device === dev);
        return devices.length > 0 ? devices[0].name : strings.training.unknownDevice;
    };

    selectMeter = (props) => {
        const { selectedMeter, selectedMeters } = this.state;
        const { userId, metersData, preselectedMeterIds, fetchAllDevices, fetchAllSwitches, visiblePeriod, setLastPreselectedMeter, setSelectedMeterIds } = props;

        if (
            selectedMeter === null && metersData && metersData.size > 0 &&
            userId !== undefined
        ) {
            const firstMeterId =  metersData.get(0).get('id');
            let meterIds = [ firstMeterId ];

            if (preselectedMeterIds && preselectedMeterIds.length > 0) {
                meterIds = preselectedMeterIds;
                setLastPreselectedMeter(meterIds[0]);
                setSelectedMeterIds(meterIds);
            } else {
                setLastPreselectedMeter(meterIds[0]);
                setSelectedMeterIds(meterIds);
            }
            if (meterIds !== undefined && meterIds.length > 0) {
                fetchAllDevices(meterIds[0]);
                this.setState(() => ({
                    selectedMeter: meterIds[0],
                    selectedMeters: meterIds
                }));
                if(this.loop === null) this.dataLoadLoop(selectedMeters);
                fetchAllSwitches(meterIds[0]);

                let preSelectedPeriod = {
                    period: 5,
                    from: 0,
                    to: 0
                };

                if (visiblePeriod !== undefined && visiblePeriod.get !== undefined){
                    preSelectedPeriod = {
                        period: visiblePeriod.get('period'),
                        from: visiblePeriod.get('from'),
                        to: visiblePeriod.get('to')
                    };
                }

                this.changePeriod(preSelectedPeriod, meterIds[0]);
            }
        }
    };

    handleMultiMeterChange = ids => {
        const { setLastPreselectedMeter, fetchAllDevices, onMonitoringSetLastTs, isLive, fetchAllSwitches, setSelectedMeterIds, isMobile} = this.props;
        onMonitoringSetLastTs(0);
        setLastPreselectedMeter(ids[0]);
        setSelectedMeterIds(ids);
        this.setState(() => ({
            selectedMeter: ids[0],
            selectedMeters: ids
        }));

        if (isMobile) {
            window.ReactNativeWebView.postMessage(JSON.stringify({meterId: ids[0]}));
        }

        if (ids[0] !== undefined) {
            fetchAllDevices(ids[0]);
            this.dataLoadLoop(ids, true);
            fetchAllSwitches(ids[0]);
        }
    };

    handleMeterChange = id => {
        const { setLastPreselectedMeter, fetchAllDevices, onMonitoringSetLastTs, isLive, fetchAllSwitches, setSelectedMeterIds, isMobile} = this.props;
        onMonitoringSetLastTs(0);
        setLastPreselectedMeter(id);
        setSelectedMeterIds([id]);
        this.setState(() => ({
            selectedMeter: id,
            selectedMeters: [id]
        }));

        if (isMobile) {
            window.ReactNativeWebView.postMessage(JSON.stringify({meterId: id}));
        }

        if (id !== undefined) {
            fetchAllDevices(id);
            this.dataLoadLoop([id], true);
            fetchAllSwitches(id);
        }
    };

    handleSwitchChange = id => {
        const { onSelectSwitch, onMonitoringSetLastTs } = this.props;
        onMonitoringSetLastTs(0);
        onSelectSwitch(id);
        this.dataLoadLoop(-1 , true);
    };

    handleRefreshChange = id => {
        const { setLastPreselectedRefresh } = this.props;
        setLastPreselectedRefresh(id);
        this.dataLoadLoop(-1, false, id);
    };

    dataLoadLoop = (meterIds = null, force = false, refresh = -1) => {
        const { fetchMonitoringRawData, refreshRate, isLive, isLoading } = this.props;
        const { selectedMeters } = this.state;
        const secs = refresh < 0 ? refreshRate : refresh;
        if (meterIds === null || meterIds === -1 || meterIds[0] < 0) {
            meterIds = selectedMeters;
        }
        if(!isLoading && (isLive || force) && (meterIds && meterIds[0] >= 0))
            fetchMonitoringRawData(meterIds).then( () => {
                clearTimeout(this.loop);
                this.setNextCall(secs);
            }).catch(error => {
                this.setNextCall(30);
            });
        else this.setNextCall(secs);
    };

    setNextCall = refreshRate => {
        this.loop = setTimeout(this.dataLoadLoop, refreshRate * 1000);
    };

    goLive = () => {
        const { graphBounds, onMonitoringSetBounds } = this.props;
        onMonitoringSetBounds(graphBounds.from - graphBounds.to, 0); // shift to end
    };

    zoomIn = () => {
        const { graphBounds, onMonitoringSetLastTs, onMonitoringSetBounds, isLive, onChangeVisiblePeriod } = this.props;
        const space = (graphBounds.from - graphBounds.to);
        if(space > 60000){
            // at least 1 min
            onMonitoringSetLastTs(0);
            const zoom = Math.round(space / 6);
            if(isLive){
                // zoom to the left
                const from = graphBounds.from - 2 * zoom;
                const { to } = graphBounds;
                onMonitoringSetBounds(from, to);
                onChangeVisiblePeriod({ period: 0, from, to});
            } else {
                // zoom both sides
                const from = graphBounds.from - zoom;
                const to = graphBounds.to + zoom;
                onMonitoringSetBounds(from, to);
                onChangeVisiblePeriod({ period: 0, from, to});
            }
            this.dataLoadLoop(-1, true, -1);
        }
    };

    zoomOut = () => {
        const { graphBounds, onMonitoringSetLastTs, onMonitoringSetBounds, isLive, onChangeVisiblePeriod } = this.props;
        const space = (graphBounds.from - graphBounds.to);
        if(space < 86400000){
            // 24 hours
            onMonitoringSetLastTs(0);
            const zoom = Math.round(space / 4);
            if(isLive){
                // zoom to the left
                const from = graphBounds.from + 2 * zoom;
                onMonitoringSetBounds(from, graphBounds.to);
                onChangeVisiblePeriod({ period: 0, from, to: graphBounds.to});
            } else {
                // zoom both sides
                let to = graphBounds.to - zoom;
                let from = graphBounds.from + zoom;
                if(to < 0){
                    from -= to;
                    to = 0;
                }
                onMonitoringSetBounds(from, to);
                onChangeVisiblePeriod({ period: 0, from, to});
            }

            this.dataLoadLoop(-1, true, -1);
        }
    };

    dragStart = e => {
        const {graphBounds} = this.props;
        this.dragStartPoint = e.clientX;
        this.dragFrom = graphBounds.from;
        this.lastDragFrom = graphBounds.from;
        this.dragTo = graphBounds.to;
        this.isDragging = true;
    };

    dragging = e => {
        if(this.isDragging){
            const { onMonitoringIsLive, isLive, onMonitoringSetBounds, onChangeVisiblePeriod } = this.props;
            if(isLive) onMonitoringIsLive(false);
            const len = e.clientX - this.dragStartPoint;
            const width = this.dragBox.current.offsetWidth;
            const step = Math.round((this.dragFrom - this.dragTo) / 100);
            const distance = Math.round(len * (this.dragFrom - this.dragTo) / width);
            const move = Math.abs((this.dragFrom + distance) - this.lastDragFrom);
            if(move > step) {
                const currentTs = moment().valueOf();
                this.lastDragFrom = this.dragFrom + distance;
                onMonitoringSetBounds(this.dragFrom + distance, this.dragTo + distance);
                onChangeVisiblePeriod({ period: 0, from: currentTs - (this.dragFrom + distance), to: currentTs - (this.dragTo + distance)});
            }
            this.dragged = true;
        }
    };

    dragEnd = e => {
        this.isDragging = false;
        if(this.dragged) {
            const {onMonitoringSetLastTs} = this.props;
            onMonitoringSetLastTs(0);
            this.dragged = false;
            this.dataLoadLoop(-1, true, -1);
        }
    };

    showPeriodSelectModal = () => {
        this.setState({
            showPeriodSelect: true,
        });
    };

    hidePeriodSelectModal = () => {
        this.setState({
            showPeriodSelect: false
        });
    };

    showSvgSelectModal = () => {
        this.setState({
            showSvgSelect: true
        });
    };

    hideSvgSelectModal = () => {
        this.setState({
            showSvgSelect: false
        });
    };

    showExcelSelectModal = () => {
        this.setState({
            showExcelSelect: true
        });
    };

    hideExcelSelectModal = () => {
        this.setState({
            showExcelSelect: false
        });
    };

    changePeriodPast = (values) => {
        const { onMonitoringSetBounds, onMonitoringIsLive, onChangeVisiblePeriod } = this.props;

        const currentTs = moment().valueOf();
        const from = values.from.valueOf();
        const to = values.to.valueOf();
        onMonitoringSetBounds(currentTs - from, currentTs - to);
        onChangeVisiblePeriod({ period: values.period, from, to});
        onMonitoringIsLive(false);
    }

    changePeriodLive = (values) => {
        const { onMonitoringIsLive } = this.props;

        const periodIsNumber = Number.isInteger(values.period);

        if(periodIsNumber === true) {
            this.changePeriodFixed(values);
        } else {
            this.changePeriodFlexible(values);
        }

        onMonitoringIsLive(true);
    }

    changePeriodFixed = (values) => {
        const { onMonitoringSetBounds, onChangeVisiblePeriod } = this.props;

        onMonitoringSetBounds(values.period * 60000, 0);
        onChangeVisiblePeriod({ period: values.period, from: values.period * 60000, to: 0});
    }

    changePeriodFlexible = (values) => {
        const { onMonitoringSetBounds, onChangeVisiblePeriod } = this.props;

        const currentTs = moment().valueOf();
        const from = values.from === null ? 0 : values.from.valueOf();
        const to = values.to.valueOf() === 0 ? currentTs : values.to.valueOf();

        onMonitoringSetBounds(currentTs - from, currentTs - to);
        onChangeVisiblePeriod({ period: values.period, from, to: values.to.valueOf()});
    }

    changePeriod = (values, meterId = -1) => {
        const { onMonitoringSetLastTs, preSelectedChartMode } = this.props;
        const oneDayMilliseconds = 86400000;

        onMonitoringSetLastTs(0);

        if(values.period === 0 || (values.to !== undefined && values.to !== 0)){
            this.changePeriodPast(values);
        } else {
            this.changePeriodLive(values);
        }

        if (values.to !== undefined) {
            const to = values.to.valueOf() === 0 ? moment().valueOf() : values.to.valueOf();

            if (to - values.from > oneDayMilliseconds && preSelectedChartMode === CHART_MODE.devices) {
                this.onChartModeChanged(true, undefined);
            }
        }

        this.dataLoadLoop([meterId], true, -1);

        this.setState({
            showPeriodSelect: false
        });
    };

    isChartToggleDisabled = () => {
        const { period } = this.props;
        const oneDayMilliseconds = 86400000;

        if (period.to.valueOf() !== undefined) {
            const to = period.to.valueOf()  === 0 ? moment().valueOf() : period.to.valueOf() ;

            if (to - period.from.valueOf() > oneDayMilliseconds) {
                return true;
            }
        }

        return false;
    }

    getPeriodText = () => {
        const { visiblePeriod, period, isMobile } = this.props;
        const { width } = this.state;

        switch(visiblePeriod.get('period')){
            case 2:
                return strings.training.last2min;
            case 5:
                return strings.training.last5min;
            case 10:
                return strings.training.last10min;
            case 30:
                return strings.training.last30min;
            case 60:
                return strings.training.last1hour;
            case 180:
                return strings.training.last3hours;
            case 360:
                return strings.training.last6hours;
            case 720:
                return strings.training.last12hours;
            case 1440:
                return strings.training.last24hours;
            case periods.today:
                return strings.training.today;
            case periods.yesterday:
                return strings.training.yesterday;
            case periods.thisWeek:
                return strings.training.thisWeek;
            case periods.lastWeek:
                return strings.training.lastWeek;
            case periods.thisMonth:
                return strings.training.thisMonth;
            case periods.lastMonth:
                return strings.training.lastMonth;
            case periods.thisQuarter:
                return strings.training.thisQuarter;
            case periods.lastQuarter:
                return strings.training.lastQuarter;
            case periods.thisYear:
                return strings.training.thisYear;
            case periods.lastYear:
                return strings.training.lastYear;
            default:
                return isMobile || (width <= 1230 && width > 1200) ?
                <div>
                    <div>
                        {`${period.from.format('DD.MM.YYYY HH:mm:ss')} ->`}
                    </div>
                    <div>
                        {`${period.to.format('DD.MM.YYYY HH:mm:ss')}`}
                    </div>
                </div>
                : `${period.from.format('DD.MM.YYYY HH:mm:ss')} -> ${period.to.format('DD.MM.YYYY HH:mm:ss')}`;
        }
    };

    wrongClientTime = () => {
        const { timeDifference } = this.props;
        return timeDifference > 60000;
    };

    isPremium = () => {
        const { cloudPlan } = this.props;
        return cloudPlan === 'P';
    };

    isStandard = () => {
        const { cloudPlan } = this.props;
        return cloudPlan === 'S';
    };

    changeEnergyFlowPosition = (newPosition) => () => {
        const { setSelectedEnergyFlowPosition } = this.props;

        this.setState(() => ({
            energyFlowPosition: newPosition,
        }));

        setSelectedEnergyFlowPosition(newPosition);
    }

    onChartModeChanged = (checked, _) => {
        const {setSelectedChartMode, preSelectedChartMode, onClearRawData } = this.props;
        const newMode = checked === true ? CHART_MODE.phases : CHART_MODE.devices;

        setSelectedChartMode(newMode);
        if (preSelectedChartMode === CHART_MODE.phases && newMode !== CHART_MODE.phases) {
            onClearRawData();
            this.dataLoadLoop(-1, true);
        }
    };

    renderChart = () => {
        const { energyFlowPosition } = this.state;
        const { graphData, rawData, isLoading, isMobile, preSelectedChartMode } = this.props;

        if (isMobile) {
            this.options.events = ["click"];
        }

        const submetersAvailable = rawData.recognitions.find(item => (item.device < 0));

        const chartDataSource = preSelectedChartMode === CHART_MODE.devices
        ? submetersAvailable
            ? { datasets: graphData.devicesAndSubmeters.filter(dataSet => dataSet.isMeter)}
            : { datasets: graphData.devicesAndSubmeters}
        : graphData;

        const chartOptions = preSelectedChartMode === CHART_MODE.devices
        ? this.optionsDevice
        : this.options;

        return <Col span={energyFlowPosition === 'left' || energyFlowPosition === 'right'  ? 12 : undefined}
                    onMouseDown={isMobile ? undefined : this.dragStart}
                    onMouseUp={isMobile ? undefined : this.dragEnd}
                    onMouseMove={isMobile ? undefined : this.dragging}
        >
            <Row gutter={5} style={{display: 'flex', marginTop: 5}}>
                <Col>
                    <Switch style={{marginLeft: 10}}
                            onChange={this.onChartModeChanged}
                            checkedChildren={strings.dashboard.chartModePhases}
                            unCheckedChildren={strings.dashboard.chartModeDevice}
                            checked={preSelectedChartMode === CHART_MODE.phases}
                            defaultChecked={preSelectedChartMode === CHART_MODE.phases}
                            disabled={this.isChartToggleDisabled()}/>
                </Col>
                <Col style={{color: 'red'}}>
                    {rawData.phaseSequenceMessage}
                </Col>
            </Row>
            <div ref={this.dragBox}
                 style={{ height: 600}}>
                <Line ref={this.chart} data={chartDataSource} options={chartOptions} />
            </div>
            {   /* Here is a dirty hack for allowing mobile app to scroll over the graph, but allow clicking legends.
                           According to documentation of chartjs it should be possible to only allow click-events by setting the
                           events option accordingly. This does not work as we expect though.
                           The hack also doesn't work perfectly as scroll doesn't work when touching directly the legends.
                        */
                isMobile ?
                    <>
                        <div style={{...this.mobilePreventTouchScrollBox, height: 570, marginTop: 108}}/>
                        <div style={{...this.mobilePreventTouchScrollBox, height: 34, marginLeft: 80}}/>
                        <div style={{...this.mobilePreventTouchScrollBox, height: 21, marginTop: 36}}/>
                        <div style={{...this.mobilePreventTouchScrollBox, width: 48, height: 13, marginTop: 57}}/>
                        <div style={{...this.mobilePreventTouchScrollBox, marginLeft: 300, height: 13, marginTop: 57}}/>
                        <div style={{...this.mobilePreventTouchScrollBox, height: 25, marginTop: 70}}/>
                        <div style={{...this.mobilePreventTouchScrollBox, width: 48, height: 13, marginTop: 95}}/>
                        <div style={{...this.mobilePreventTouchScrollBox, marginLeft: 300, height: 13, marginTop: 95}}/>
                    </>

                    : undefined
            }


            {
                /* this.wrongClientTime() ?
                    <div style={fillBox}>
                        <div style={errorBoxContent}>{strings.dashboard.wrongTime}</div>
                    </div> : */

                (!rawData || !rawData.powerData || rawData.powerData.length === 0) && !isLoading ?
                    <div style={fillBox}>
                        <div style={fillBoxContent}>{strings.dashboard.noData}</div>
                    </div> : null
            }
        </Col>
    }

    renderEnergyFlow = (position) => {
        const { energyFlowPosition } = this.state;
        const { isMobile, isLoading, isLive, rawData} = this.props;

        const energyFlowSpan = energyFlowPosition === 'up' || energyFlowPosition === 'down' ? undefined : 12;

        return position === energyFlowPosition ? <Col span={energyFlowSpan}>
            <div style={{width: "100%", display:"flex", justifyContent:"center"}}>
                { (!isLoading || isLive && rawData.powerData.length !== 0) &&
                    <EnergyFlow
                        width="100%"
                        position={position}
                        changePosition={this.changeEnergyFlowPosition}
                        isMobile={isMobile} />
                }
            </div>
        </Col> : undefined
    }

    renderChartAndEnergyFlow = () => <>
            {this.renderEnergyFlow('up')}
            <Col>
                <Row gutter={[16, 16]}>
                    {this.renderEnergyFlow('left')}
                    {this.renderChart()}
                    {this.renderEnergyFlow('right')}
                </Row>
            </Col>
            {this.renderEnergyFlow('down')}
    </>;

    getMenuFlowDirection = () => window.innerWidth <= 1200? 'column' : 'row';

    getPeriodSelectorMargin = () => window.innerWidth <= 1200 ? 0 : 'auto';

    renderProgressBars = (energyBought, energyUsed, breakLine) => {
        const { rawData, graphData } = this.props;

        const pvEfficiency = Math.round((rawData.solarTotalKwh === 0 ? 0 : energyUsed/rawData.solarTotalKwh) * 1000) / 10;
        const pvColor = pvEfficiency < 35 ? '#ff3645' : pvEfficiency < 70 ? '#ffa037' : '#82D448';

        const batteryData = graphData.datasets.find(data => data.label === "BATT");
        const latestBatteryTs = Math.max(...batteryData.data.map(o => o.x));
        const latestBattery = batteryData.data.find(data => data.x === latestBatteryTs);
        const batteryStateOfCharge = latestBattery && latestBattery.soc ? latestBattery.soc : 0;

        const autonomy = (energyUsed + energyBought) > 0 ? (energyUsed)  / (energyUsed + energyBought) * 100 : 0;

        const progressBarSpan = breakLine ? undefined : 12;

        return <Col span={progressBarSpan}>
            <p style={{margin: '1em 0 0 0'}}>{strings.training.completeness24h}:</p><Progress strokeColor={graphColors.oa_line} percent={Math.round(rawData.completeness24h * 10) / 10} />
            <p style={{margin: '1em 0 0 0'}}>{strings.training.completeness1h}:</p><Progress strokeColor={graphColors.oa_line} percent={Math.round(rawData.completeness1h * 10) / 10} />
            <p style={{margin: '1em 0 0 0'}}>{strings.training.recognitionRate}:</p><Progress strokeColor='#0e8220' percent={Math.round(rawData.recognitionRate * 10) / 10} />
            <p style={{margin: '1em 0 0 0'}}>{strings.training.recognitionCorrectness}:</p><Progress strokeColor='#0e8220' percent={Math.round(rawData.recognitionCorrectness * 10) / 10} />
            <p style={{margin: '1em 0 0 0'}}>{strings.training.classificationRate}:</p><Progress strokeColor='#046aff' percent={Math.round(rawData.classificationRate * 10) / 10} />
            <p style={{margin: '1em 0 0 0'}}>{strings.dashboard.pvOwnUsage}:</p><Progress strokeColor={pvColor} percent={pvEfficiency} />
            <p style={{margin: '1em 0 0 0'}}>{strings.dashboard.autonomy}:</p><Progress strokeColor='#6BB642' percent={Math.round(autonomy * 10) / 10} />
            <p style={{margin: '1em 0 0 0'}}>{strings.dashboard.batteryCharge}:</p><Progress strokeColor={graphColors.battery_line} percent={batteryStateOfCharge} />
        </Col>
    }

    renderEfficiency = (energySold, energyBought, energyUsed) => {
        const { isMobile } = this.props;

        const efficiencyData = {
            labels: [strings.dashboard.pvEfficiency],
            datasets: [{
                label: strings.dashboard.pvProduced,
                backgroundColor: '#ffa037',
                stack: 'Stack 0',
                data: [Math.round(energyUsed * 10000) / 10000]
            }, {
                label: strings.dashboard.pvSold,
                backgroundColor: '#0e8220',
                stack: 'Stack 0',
                data: [Math.round(energySold * 10000) / 10000]
            }, {
                label: strings.dashboard.pvUsed,
                backgroundColor: '#046aff',
                stack: 'Stack 1',
                data: [Math.round(energyUsed * 10000) / 10000]
            }, {
                label: strings.dashboard.pvBought,
                backgroundColor: '#ff3645',
                stack: 'Stack 1',
                data: [Math.round(energyBought * 10000) / 10000]
            }]
        };

        return isMobile ? undefined :
            <div style={{
                height: 180,
                marginTop: 20
            }}>
                <HorizontalBar options={this.usageOptions} data={efficiencyData} />
            </div>;
    }

    renderRecognitionBars = (breakLine) => {
        const { rawData, isMobile } = this.props;

        const barData = {
            labels: [strings.training.totalKwh, strings.training.recognitionKwh, strings.training.classificationKwh, strings.training.alwaysOnKwh],
            datasets: [{
                backgroundColor: [graphColors.oa_line, '#0e8220', '#046aff', '#FF6600'],
                barThickness: 40,
                maxBarThickness: 50,
                data: [
                    Math.round(rawData.totalKwh * 10000) / 10000,
                    Math.round(rawData.recognitionKwh * 10000) / 10000,
                    Math.round(rawData.classificationKwh * 10000) / 10000,
                    Math.round(rawData.alwaysOnKwh * 10000) / 10000
                ]
            }]
        };

        const recognitionBarsSpan = breakLine ? undefined : 12;

        return <Col span={recognitionBarsSpan} >
            <div style={{
                        height: 470
                    }}>
                <Bar options={this.barOptions} data={barData} />
            </div>
            {
                isMobile ? <div style={this.mobilePreventTouchScrollBox}/> : undefined
            }
        </Col>;
    }

    renderStatistics = () => {
        const { rawData } = this.props;

        const energySold = rawData.solarSoldKwh;
        const energyBought = rawData.boughtKwh;
        const energyUsed = rawData.solarTotalKwh - energySold;

        const breakLine = window.innerWidth <= 700;

        return <Col>
            <div style={{padding: '10px 30px 10px'}}>
                <Row gutter={[16, 16]}>
                    {this.renderProgressBars(energyBought, energyUsed, breakLine)}
                    {breakLine ? undefined : this.renderRecognitionBars(breakLine)}
                </Row>
                <Row>
                    {breakLine ? this.renderRecognitionBars(breakLine) : undefined}
                </Row>
                <Row>
                    {this.renderEfficiency(energySold, energyBought, energyUsed)}
                </Row>
            </div>
        </Col>
    }

    getRecognitionDataWeb = () => {
        const { rawData } = this.props;
        const submetersAvailable = rawData.recognitions.find(item => (item.device < 0));

        return rawData.recognitions.flatMap((item, idx) => {
            if (!submetersAvailable || item.device < 0) {
                return [{
                    key: idx,
                    name: item.name,
                    count: item.count,
                    watt: `${Math.round(item.watt * 100) / 100} ${strings.training.wattUnit}`,
                    wh: `${Math.round(item.wattHours * 100) / 100} Wh`,
                    wattPercent: `${Math.round(item.wattPercent * 1000) / 10} %`
                }]
            }
            return [];
        });
    }

    getRecognitionDataMobile = () => {
        const { graphData } = this.props;

        const sortedDevices = getOnlySubmetersIfExists(graphData.devices).sort((a,b) => b.watt - a.watt);

        const usageData = graphData.datasets.find(data => data.label === "SUM");
        const latestUsageTs = Math.max(...usageData.data.map(o => o.x));
        const latestUsage = usageData.data.find(data => data.x === latestUsageTs);
        const latestUsageWatt = latestUsage ? latestUsage.y : 0;

        let deviceWatt = 0;

        sortedDevices.map(item => {
            deviceWatt += item.watt;
        })

        const recognitions = sortedDevices.map(item => ({
            key: `device-${item}`,
            name: item.name,
            watt: `${Math.round(item.watt * 100) / 100} ${strings.training.wattUnit}`,
            percent: `${Math.round(item.watt / latestUsageWatt * 1000) / 10} %`
        }));

        const otherDevicesWatt = latestUsageWatt - deviceWatt;

        if (otherDevicesWatt > 0){
            recognitions.push({
                key: "device-others",
                name: strings.dashboard.otherDevices,
                watt: `${Math.round(otherDevicesWatt * 100) / 100} ${strings.training.wattUnit}`,
                percent: `${Math.round(otherDevicesWatt / latestUsageWatt * 1000) / 10} %`
            });
        }

        return recognitions;
    }

    renderRecognicedDevices = (breakLine) => {
        const { isMobile } = this.props;

        const recognitionData = isMobile ? this.getRecognitionDataMobile() : this.getRecognitionDataWeb();

        const devicesSpan = breakLine ? undefined : 12;
        const columns = isMobile ? this.recognitionsColumnsMobile : this.recognitionsColumns;

        return <Col span={devicesSpan}>
            <p style={{textAlign: 'center', fontWeight: 'bold'}}>{isMobile ? strings.training.recognitionsMobile : strings.training.recognitions}</p>
            <Table columns={columns} dataSource={recognitionData} pagination={false} />
        </Col>;
    }

    renderAlwaysOnDevices = (breakLine) => {
        const { rawData, isMobile } = this.props;

        if (isMobile) {
            return undefined;
        }

        const alwaysOnData = rawData.alwaysOn.map((item, idx) => ({
            key: idx,
            day: item.day,
            p1: `${Math.round(item.p1 * 100) / 100} ${strings.training.wattUnit}`,
            p2: `${Math.round(item.p2 * 100) / 100} ${strings.training.wattUnit}`,
            p3: `${Math.round(item.p3 * 100) / 100} ${strings.training.wattUnit}`
        }));

        const devicesSpan = breakLine ? undefined : 12;

        return <Col span={devicesSpan} >
                <p style={{textAlign: 'center', fontWeight: 'bold'}}>{strings.training.alwaysOnPerDay}</p>
                <Table columns={this.alwaysOnColumns} dataSource={alwaysOnData} pagination={false} />
            </Col>
    }

    renderDevices = () => {
        const breakLine = window.innerWidth <= 700;

        return <Col>
            <div style={{padding: '20px 10px'}}>
                <Row gutter={[16, 16]}>
                    {this.renderRecognicedDevices(breakLine)}
                    {breakLine ? undefined : this.renderAlwaysOnDevices(breakLine)}
                </Row>
                <Row>
                    {breakLine ? this.renderAlwaysOnDevices(breakLine) : undefined}
                </Row>
            </div>
        </Col>;
    }

    renderSubscription = () => {

        const {metersData, isMobile} = this.props;
        const {selectedMeter} = this.state;

        if (isMobile === true) {
            return undefined;
        }

        let subscription = '';
        let subscriptionColor = '#d7dcd3';

        if (selectedMeter !== null) {
            const meters = metersData && metersData.toJS ? metersData.toJS() : [];
            const meter = meters.filter(item => item.id === selectedMeter);
            if (meter && meter.length > 0) {
                const subscriptionUntil = meter[0].subscriptionUntil || 0;
                const now = moment().valueOf();
                const until = moment(subscriptionUntil).format('YYYY-MM-DD');
                if (subscriptionUntil < now) {
                    // expired -> Basic
                    subscription = 'BASIC';
                } else
                    switch (meter[0].subscriptionType) {
                        case 'B':
                            subscription = 'BASIC';
                            subscriptionColor = '#d7dcd3';
                            break;
                        case 'S':
                            subscription = `STANDARD ${strings.dashboard.until} ${until}`;
                            subscriptionColor = '#bbcf29';
                            break;
                        case 'P':
                            subscription = `PREMIUM ${strings.dashboard.until} ${until}`;
                            subscriptionColor = '#96b12c';
                            break;
                        default:
                    }
            }
        }

        return <div style={{display: 'flex', alignSelf: 'center', marginTop: 17}}>
            <span style={{ marginRight: 10, marginLeft: 15 }}>{strings.dashboard.cloudSubscription}:</span>
            <div style={{backgroundColor: subscriptionColor, display: 'inline-block', borderRadius: 5}}>
                <span style={{ marginRight: 10, marginLeft: 10 }}>{subscription}</span>
            </div>
        </div>
    }

    renderZoom = (margin) => <div style={{display: 'flex', alignSelf: 'center', marginTop: margin}}>
        <Tooltip placement="bottom" title={strings.dashboard.zoomIn}>
            <Button
                type='default'
                style={{marginLeft: 10}}
                shape="circle"
                icon='zoom-in'
                onClick={this.zoomIn}
            />
        </Tooltip>
        <Tooltip placement="bottom" title={strings.dashboard.zoomOut}>
            <Button
                type='default'
                style={{marginLeft: 10}}
                shape="circle"
                icon='zoom-out'
                onClick={this.zoomOut}
            />
        </Tooltip>
    </div>;

    renderMeterSelection = () => {
        const {metersData, isAdmin, isLoading, isLive, isMobile} = this.props;
        const {selectedMeter, selectedMeters} = this.state;

        if (isMobile) {
            return undefined;
        }

        return <div style={{display: 'flex', alignSelf: 'center', marginBottom: 6}}>
            {this.isPremium() &&
            <MeterMultiSelectBox
                meterChange={this.handleMultiMeterChange}
                selectedMeters={selectedMeters}
                metersData={metersData}
                isAdmin={isAdmin}
            />
            }
            {!this.isPremium() &&
            <MeterSelectBox
                meterChange={this.handleMeterChange}
                selectedMeter={selectedMeter}
                metersData={metersData}
                isAdmin={isAdmin}
            />
            }
            <div style={{display: 'flex', alignSelf: 'center', marginTop: '17px'}}>
                <Tooltip placement="bottom" title={strings.dashboard.liveLoading}>
                    <Button
                        type={isLive ? 'primary' : 'default'}
                        style={{marginLeft: 10}}
                        shape="circle"
                        icon={isLoading ? "loading" : "redo"}
                        onClick={() => {
                            const { onMonitoringIsLive } = this.props;
                            if(!isLive){
                                this.goLive();
                                this.dataLoadLoop(selectedMeter, true);
                            }
                            onMonitoringIsLive(!isLive);
                        }}
                    />
                </Tooltip>
            </div>
        </div>
    }

    renderSwitchPremiumSelection = () => {
        const {switchList, isMobile} = this.props;
        const {selectedSwitch} = this.state;

        if (isMobile || !switchList) {
            return undefined;
        }

        return <div style={{display: 'flex', alignSelf: 'center', marginTop: 17}}>
                <span style={{ marginRight: 10, marginLeft: 15 }}>{strings.dashboard.secondGraph}:</span>
            <Select
                onChange={this.handleSwitchChange}
                dropdownMatchSelectWidth={false}
                style={{
                    minWidth: '130px'
                }}
            >
                <Select.Option value={-7} key='switch--7'>...</Select.Option>
                {
                    this.isPremium() && <Select.OptGroup label={strings.dashboard.premiumData}>
                        <Select.Option value={-6} key='switch--6'>{this.getSecondGraphLabel(-6)}</Select.Option>
                        <Select.Option value={-5} key='switch--5'>{this.getSecondGraphLabel(-5)}</Select.Option>
                        <Select.Option value={-4} key='switch--4'>{this.getSecondGraphLabel(-4)}</Select.Option>
                        <Select.Option value={-3} key='switch--3'>{this.getSecondGraphLabel(-3)}</Select.Option>
                    </Select.OptGroup>
                }
                <Select.OptGroup label={strings.dashboard.switchData}>
                {
                    switchList.map(item => <Select.Option value={item.id} key={`switch-${item.id}`}>
                        {item.brand}
                    </Select.Option>)
                }
                </Select.OptGroup>
            </Select>
        </div>
    }

    renderRefreshSelection = () => {
        const {isLive, refreshRate, isMobile} = this.props;

        if (!isLive || isMobile) {
            return undefined;
        }

        return <div style={{display: 'flex', alignSelf: 'center', marginTop: 17}}>
            <span style={{ marginRight: 10, marginLeft: 15 }}>{strings.dashboard.refreshPeriod}</span>
            <Select
                onChange={val => this.handleRefreshChange(val)}
                value={refreshRate}
                dropdownMatchSelectWidth={false}
                style={{
                    minWidth: '130px'
                }}
            >
                <Select.Option value={1} key='key-0'>
                    {strings.dashboard.every1secs}
                </Select.Option>
                <Select.Option value={2} key='key-1'>
                    {strings.dashboard.every2secs}
                </Select.Option>
                <Select.Option value={5} key='key-2'>
                    {strings.dashboard.every5secs}
                </Select.Option>
                <Select.Option value={10} key='key-3'>
                    {strings.dashboard.every10secs}
                </Select.Option>
                <Select.Option value={30} key='key-4'>
                    {strings.dashboard.every30secs}
                </Select.Option>
                <Select.Option value={60} key='key-5'>
                    {strings.dashboard.every60secs}
                </Select.Option>
            </Select>
        </div>
    }

    renderPeriodSelection = () => {

        const {isMobile} = this.props;

        return <div style={{display: 'flex', flexDirection: 'row'}}>
            <div style={{marginRight: 15, marginBottom: 0, }}>
                {isMobile ? this.renderZoom(0) : undefined}
            </div>
            <Button
                type='default'
                style={{marginRight: 10, height: 'auto'}}
                icon='calendar'
                onClick={this.showPeriodSelectModal}>
                    {this.getPeriodText()}
            </Button>
        </div>
    }

    selectPeriodAndMeter = (fileUploadResponse) => {
        console.log("Selecting: ", JSON.stringify(fileUploadResponse, null, 4));

        const {from, to, meterId} = fileUploadResponse;
        const { onMonitoringIsLive, fetchAllMeters } = this.props;

        fetchAllMeters()
            .then(res => {
                this.handleMultiMeterChange([meterId]);
                this.changePeriod({from, to}, meterId);
                onMonitoringIsLive(true);
            })
    }

    renderFileUpload = () => {
        const { addUrlAndToken } = this.props;

        const onUpdate = (response) => this.selectPeriodAndMeter(response);

        const props = {
            name: 'file',
            headers: {
                authorization: 'authorization-text'
            },
            onChange: (info) => {
              if (info.file.status !== 'uploading') {
                // message.success(`${info.file.name} ${strings.dashboard.uploading}`);
                console.log(info.file, info.fileList);
              }
              if (info.file.status === 'done') {
                message.success(`${info.file.name} ${strings.dashboard.uploadStarted}`);
                const { response } = info.file;
                onUpdate(response);
              } else if (info.file.status === 'error') {
                message.error(`${info.file.name} ${strings.dashboard.uploadFailed}`);
              }
            },
            showUploadList: false
        };

        const finalProps = addUrlAndToken(props);

        return <div style={{marginLeft: 5}}>
            <Upload {...finalProps} >
                <Tooltip placement="bottom" title={strings.dashboard.uploadLoadprofile}>
                    <Button type='primary' icon='cloud-upload'/>
                </Tooltip>
            </Upload>
        </div>
    }

    render() {
        const {
            showPeriodSelect,
            showSvgSelect,
            showExcelSelect,
            width
        } = this.state;
        const {
            metersData,
            isLoading,
            isLive,
            graphBounds,
            rawData,
            switchListIsLoading,
            selectedSwitch,
            singleGraphData,
            addonData,
            isMobile,
        } = this.props;

        let unit = 'second';
        const len = Math.round((graphBounds.from - graphBounds.to) / 1000);
        if(len < 90) // 1.5 Minutes
            unit = 'second';
        else if(len < 5400) // 1.5 Hours
            unit = 'minute';
        else if(len < 36 * 3600) // 1.5 Days
            unit = 'hour';
        else
            unit = 'day';

        this.options.scales.xAxes[0].time.unit = unit;

        // WA-3144 adapt MeterSelectBox to multi select
        return metersData && metersData.size ? ([
            <Card key="0" bodyStyle={{padding: isMobile ? 3 : 24}}>
                <div style={{display: 'flex', flexDirection: this.getMenuFlowDirection()}}>
                        {this.renderMeterSelection()}
                        {this.renderSwitchPremiumSelection()}
                        {this.renderRefreshSelection()}
                        {isMobile === true ? undefined : this.renderZoom(17)}
                        {this.renderSubscription()}
                        <div style={{display: 'flex', marginTop: isMobile ? 0 : 17, marginLeft: this.getPeriodSelectorMargin(), alignSelf: 'center'}}>
                        {
                            (this.isPremium() || this.isStandard()) && !isMobile &&
                            <Tooltip placement="bottom" title={strings.dashboard.downloadSvg}>
                                <Button
                                    type='primary'
                                    style={{marginRight: 10}}
                                    icon='cloud-download'
                                    onClick={this.showSvgSelectModal}
                                />
                            </Tooltip>
                        }
                        {
                            this.isPremium() && !isMobile &&
                            <Tooltip placement="bottom" title={strings.dashboard.downloadExcel}>
                                <Button
                                   type='primary'
                                    style={{marginRight: 10}}
                                    icon='file-excel'
                                    onClick={this.showExcelSelectModal}
                                />
                            </Tooltip>
                        }
                        {(this.isPremium() || this.isStandard()) && !isMobile &&this.renderFileUpload()}
                        {this.renderPeriodSelection()}
                        </div>

                </div>
            </Card>,
            <Card
                bodyStyle={{ padding: 0 }}
                key="1"
            >
                <Row>

                    <Spin spinning={isLoading && (!isLive || rawData.powerData.length === 0)} size="large">
                        {this.renderChartAndEnergyFlow()}
                        {isMobile ? this.renderDevices() : undefined}
                        {
                            !switchListIsLoading && selectedSwitch > 0 &&
                            <Col>
                                <div style={{
                                    height: 300
                                }}>
                                    <Line data={singleGraphData} options={{...this.options, ...this.singleOptions}}/>
                                </div>
                                {
                                    /* this.wrongClientTime() ?
                                    <div style={fillBox}>
                                        <div style={errorBoxContent}>{strings.dashboard.wrongTime}</div>
                                    </div> : */
                                        (!rawData || (rawData.powerSingle && rawData.powerSingle.length === 0)) && !isLoading ?
                                        <div style={fillBox}>
                                            <div style={fillBoxContent}>{strings.dashboard.noData}</div>
                                        </div> : null
                                }
                            </Col>
                        }
                        {
                            !switchListIsLoading && selectedSwitch < 0 && selectedSwitch > -7 && this.isPremium() &&
                            <Col>
                                <div style={{
                                    height: 400
                                }}>
                                    <Line data={addonData} options={
                                        {
                                            ...this.options,
                                            ...this.addonOptions,
                                            scales :{...this.addonOptions.scales, yAxes:[{... this.addonOptions.scales.yAxes[0], scaleLabel: {...this.addonOptions.scales.yAxes[0].scaleLabel, labelString: this.getSecondGraphLabel(selectedSwitch)}}]},
                                        }
                                    }
                                    />
                                </div>
                                {
                                    /* this.wrongClientTime() ?
                                    <div style={fillBox}>
                                        <div style={errorBoxContent}>{strings.dashboard.wrongTime}</div>
                                    </div> : */
                                        (!rawData || (rawData.premiumData && rawData.premiumData.length === 0)) && !isLoading ?
                                        <div style={fillBox}>
                                            <div style={fillBoxContent}>{strings.dashboard.noData}</div>
                                        </div> : null
                                }
                            </Col>
                        }
                        {this.renderStatistics()}
                        {isMobile ? undefined : this.renderDevices()}
                    </Spin>
                </Row>
                <PeriodSelectModal
                    key="period-select-modal"
                    msgs={strings}
                    isShown={showPeriodSelect}
                    onCancel={this.hidePeriodSelectModal}
                    onSave={this.changePeriod}
                    isLoading={false}
                    isMobile={isMobile}
                />
                <SvgSelectModal
                    key="svg-select-modal"
                    msgs={strings}
                    isShown={showSvgSelect}
                    onCancel={this.hideSvgSelectModal}
                    isLoading={false}
                />
                <ExcelSelectModal
                    key="excel-select-modal"
                    msgs={strings}
                    isShown={showExcelSelect}
                    onCancel={this.hideExcelSelectModal}
                    isLoading={false}
                />
            </Card>]
        ) : (
            <Card>
                <Empty
                    description={<span>{strings.noDataAvailable}</span>}
                    style={{ margin: '30px auto' }}
                />
            </Card>
        );
    }
}
export default connect(
    (state, ownProps) => ({
        ...ownProps,
        metersData: getMetersListSortedForMonitoring(state),
        userId: getUserId(state),
        preselectedMeterId: getPreselectedMeterId(state),
        preselectedMeterIds: getPreselectedMeterIds(state),
        isAdmin: isRoleAdministrator(state),
        graphData: getMonitoringGraphData(state),
        isLoading: getMonitoringIsLoading(state),
        refreshRate: getPreselectedRefresh(state),
        isLive: getMonitoringIsLive(state),
        graphBounds: getMonitoringBounds(state),
        period: getMonitoringBoundsMoment(state),
        visiblePeriod: getMonitoringVisiblePeriod(state),
        rawData: getMonitoringRawData(state),
        singleGraphData: getMonitoringSingleGraphData(state),
        switchList: getSwitchList(state),
        switchListIsLoading: getSwitchListIsLoading(state),
        selectedSwitch: getSelectedSwitch(state),
        legendsVisibility: getLegendsVisibility(state),
        timeDifference: getServerTimeDifference(state),
        cloudPlan: getValidCloudPlan(state),
        addonData: getMonitoringAddonGraphData(state),
        preselectedEnergyFlowPosition: getPreSelectedEnergyFlowPosition(state),
        preSelectedChartMode: getPreSelectedChartMode(state),
}),
    {
        fetchAllMeters,
        setLastPreselectedMeter,
        setSelectedMeterIds,
        fetchAllDevices,
        fetchAllDeviceTypes,
        fetchMonitoringRawData,
        setLastPreselectedRefresh,
        onMonitoringIsLive,
        onMonitoringSetBounds,
        onMonitoringSetLastTs,
        onChangeVisiblePeriod,
        onSelectSwitch,
        fetchAllSwitches,
        setLastLegendVisibility,
        setSelectedEnergyFlowPosition,
        setSelectedChartMode,
        fetchMeterTypes,
        addUrlAndToken,
        onClearRawData,
    }
)(Monitoring);
